<div class="rma-detail-page" stack-layout [fullHeight]="true" gap="md" paddingVertical="md">
  <div box-layout paddingHorizontal="md">
    <button
      text-button
      size="md"
      symbolLeft="chevron_left"
      variant="ghost"
      [routerLink]="['/rma-management/summary']"
    >
      RMA Summary
    </button>
  </div>

  <div center-layout paddingHorizontal="lg">
    <div class="rma-detail-shell">
      @if (rmaDetail(); as detail) {
        <div class="rma-page-title-row">
          <h1 class="rma-page-title">RMA Detail</h1>

          @if (shouldShowApprovalActions(detail)) {
            <div class="rma-approval-actions">
              <button
                text-button
                variant="secondary"
                size="sm"
                symbolLeft="check_circle"
                class="rma-approve-button"
                [disable]="submittingDecision()"
                (click)="openDecisionDialog('approved')"
              >
                Approve
              </button>
              <button
                text-button
                variant="secondary"
                size="sm"
                symbolLeft="close"
                class="rma-reject-button"
                [disable]="submittingDecision()"
                (click)="openDecisionDialog('rejected')"
              >
                Reject
              </button>
            </div>
          }
          @if (shouldShowResubmitAction(detail)) {
            <div class="rma-approval-actions">
              <button text-button variant="primary" size="md" class="rma-resubmit-button" (click)="startResubmitForApproval()">
                Resubmit
              </button>
            </div>
          }
          @if (resubmitStarted()) {
            <div class="rma-approval-actions">
              <button text-button variant="primary" size="md" class="rma-resubmit-button" [disable]="submittingResubmit()" (click)="openResubmitReviewDialog()">
                Review &amp; Resubmit
              </button>
            </div>
          }
        </div>

        @if (resubmitInlineError()) {
          <div class="resubmit-inline-error">{{ resubmitInlineError() }}</div>
        }

        <div class="rma-detail-layout">
          <section class="rma-main-card">
            <header class="rma-header">
              <div>
                <div class="rma-title-row">
                  <h2 class="rma-title">{{ detail.rma_number }}</h2>
                  <span [class]="getStatusBadgeClass(detail.status)">{{ detail.status }}</span>
                </div>
              </div>
            </header>

            <div class="meta-grid">
              <div class="meta-item">
                <div class="meta-label">Order Number</div>
                <div class="meta-value">{{ getOrderNumber(detail) }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Number of Units</div>
                <div class="meta-value">{{ totalUnits() }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">RMA Type</div>
                <div class="meta-value">{{ detail.rma_type || '-' }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">{{ detail.resubmission_reason ? 'Rejection Reason' : 'Return Reason' }}</div>
                <div class="meta-value">{{ detail.reason || '-' }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">DCC</div>
                <div class="meta-value">{{ getDccCode(detail) }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Warehouse Code</div>
                <div class="meta-value">{{ getWarehouseCode(detail) }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Channel</div>
                <div class="meta-value">{{ getChannelCode(detail) }}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Compliance</div>
                <div class="meta-value">
                  <span [class]="getComplianceBadgeClass(detail.compliance_status)">
                    {{ detail.compliance_status || '-' }}
                  </span>
                </div>
              </div>
              @if (isCreditOnlyRma(detail.rma_type)) {
                <div class="meta-item">
                  <div class="meta-label">Refund Amount</div>
                  <div class="meta-value">{{ formatAmount(detail.credit_amount) }}</div>
                </div>
              }
              @if (detail.resubmission_reason) {
                <div class="meta-item">
                  <div class="meta-label">Resubmission Reason</div>
                  <div class="meta-value">
                    <div>{{ detail.resubmission_reason }}</div>
                    @if (detail.resubmission_notes) {
                      <div class="meta-subvalue">{{ detail.resubmission_notes }}</div>
                    }
                  </div>
                </div>
              }
            </div>

            @if (isRejectedRma(detail.status) && detail.rejection_reason) {
              <div class="notes-block">
                <div class="rejection-reason-alert" role="alert">
                  <span class="rejection-reason-alert-icon" aria-hidden="true">!</span>
                  <div>
                    <div class="rejection-reason-alert-title">Rejection Reason</div>
                    <div class="rejection-reason-alert-message">{{ detail.rejection_reason }}</div>
                  </div>
                </div>
              </div>
            }

            <div class="devices-block">
              <div class="devices-header">
                <div class="section-title">Device Details</div>
                <div class="devices-count">{{ totalUnits() }} devices</div>
              </div>

              <DataTable [datasource]="deviceDetailsDatasource" [columns]="deviceDetailColumns">
              </DataTable>

              <ng-template #deviceCell let-device="device" let-deviceMeta="deviceMeta">
                <div class="device-cell">
                  <div class="device-name">{{ device || '-' }}</div>
                  <div class="device-meta">{{ deviceMeta || 'IMEI / Serial: -' }}</div>
                </div>
              </ng-template>

              <ng-template #specsCell let-specsCapacity="specsCapacity" let-specsCondition="specsCondition">
                <div class="specs-cell">
                  <div class="specs-line">
                    <span class="specs-label">Capacity:</span>
                    <span class="specs-value">{{ specsCapacity || '-' }}</span>
                  </div>
                  <div class="specs-line">
                    <span class="specs-label">Condition:</span>
                    <span class="specs-value specs-value-condition">{{ specsCondition || '-' }}</span>
                  </div>
                </div>
              </ng-template>

              <ng-template #complianceCell let-compliance="compliance">
                @if (canEditComplianceStatus()) {
                  <select
                    class="compliance-select"
                    [value]="getComplianceDropdownValue(getComplianceCellStatus(compliance))"
                    (change)="onComplianceStatusChange(getComplianceCellItemId(compliance), $any($event.target).value)"
                    aria-label="Compliance status"
                  >
                    @for (option of complianceOptions; track option) {
                      <option [value]="option">{{ option }}</option>
                    }
                  </select>
                } @else {
                  <span [class]="getComplianceBadgeClass(getComplianceCellStatus(compliance))">
                    {{ getComplianceCellStatus(compliance) || '-' }}
                  </span>
                }
              </ng-template>

              <ng-template #resubmitCreditAmountCell let-itemId="itemId">
                <div class="resubmit-credit-cell">
                  <input
                    class="resubmit-credit-input"
                    [class.resubmit-credit-input--invalid]="!!getResubmitCreditValidationMessage(itemId)"
                    type="number"
                    min="0"
                    step="0.01"
                    [value]="getResubmitCreditAmount(itemId)"
                    [disabled]="isResubmitItemRemoved(itemId)"
                    (input)="onResubmitCreditAmountChange(itemId, $any($event.target).value)"
                    aria-label="Credit value"
                  />
                  @if (getResubmitCreditValidationMessage(itemId)) {
                    <div class="resubmit-credit-error">{{ getResubmitCreditValidationMessage(itemId) }}</div>
                  }
                </div>
              </ng-template>

              <ng-template #resubmitRemoveCell let-itemId="itemId">
                <button
                  type="button"
                  class="resubmit-remove-button"
                  [class.resubmit-remove-button--restore]="isResubmitItemRemoved(itemId)"
                  [class.resubmit-remove-button--disabled]="!canRemoveResubmitItems()"
                  [attr.aria-disabled]="!canRemoveResubmitItems()"
                  (click)="toggleResubmitItemRemoved(itemId)"
                >
                  {{ isResubmitItemRemoved(itemId) ? 'Restore' : 'Remove' }}
                </button>
              </ng-template>

              <div box-layout paddingTop="sm">
                <Paginator [datasource]="deviceDetailsDatasource" [initPageSize]="10" />
              </div>

              <div class="table-footer">
                <div class="table-footer-summary">Total — {{ totalUnits() }} devices</div>
                <div class="table-total-group">
                  <div class="table-total">{{ formatAmount(consolidatedUnitPrice()) }}</div>
                  <div class="table-total-label">Total Value</div>
                </div>
              </div>
            </div>

            <div class="attachments-block">
                <div class="section-title">Attachments</div>
                <app-image-attachment-slider
                  [attachments]="attachments()"
                  [loading]="attachmentsLoading()"
                  [imagesLoading]="attachmentImagesLoading()"
                  [thumbnailUrls]="attachmentThumbnailUrls()"
                  (downloadAttachment)="downloadAttachment($event)"
                  (viewerOpened)="startAttachmentThumbnailLoading($event)"
                  (viewerClosed)="stopAttachmentThumbnailLoading()"
                />
            </div>

          </section>

          <aside class="rma-activity-card">
            <div class="activity-title">Activity</div>

            <div class="activity-list">
              <span class="activity-rail" aria-hidden="true"></span>
              @for (item of timelineItems(); track item.title + '-' + item.subtitle) {
                <div class="activity-item">
                  <span class="timeline-marker" aria-hidden="true">
                    <span [class]="getTimelineDotClass(item.variant)"></span>
                  </span>
                  <div class="activity-content">
                    <div class="activity-item-title">{{ item.title }}</div>
                    <div class="activity-item-subtitle">{{ item.subtitle }}</div>
                  </div>
                </div>
              }
            </div>
          </aside>
        </div>
      } @else {
        <h1 class="rma-page-title">RMA Detail</h1>
        <div class="loading-card">
          <Text role="body-large">Loading RMA details...</Text>
        </div>
      }
    </div>
  </div>
</div>

<Dialog [show]="activeDecisionDialog() === 'approved'" (showing)="$event ? activeDecisionDialog.set('approved') : closeDecisionDialog()">
  <div class="decision-dialog decision-dialog--approve">
    <div class="decision-dialog-top">
      <div class="decision-dialog-heading">Approve RMA</div>
      <button type="button" class="decision-dialog-close" aria-label="Close" (click)="closeDecisionDialog()">×</button>
    </div>

    <div class="decision-dialog-message">
      <span class="decision-dialog-icon decision-dialog-icon--approve">✓</span>
      <div>
        <div class="decision-dialog-title">You are about to approve this RMA.</div>
        <div class="decision-dialog-subtitle">Add an optional comment below.</div>
      </div>
    </div>

    <label class="decision-dialog-label" for="approveDecisionComments">Comment (optional)</label>
    <textarea
      id="approveDecisionComments"
      class="decision-dialog-textarea"
      [value]="decisionComments()"
      [disabled]="submittingDecision()"
      [attr.maxlength]="decisionCommentsMaxLength"
      (input)="onDecisionCommentsChange($any($event.target).value)"
      placeholder="Enter comment..."
    ></textarea>
    <div class="decision-dialog-counter">{{ decisionComments().length }} / {{ decisionCommentsMaxLength }}</div>

    <div class="decision-dialog-actions">
      <button text-button variant="ghost" size="md" [disable]="submittingDecision()" (click)="closeDecisionDialog()">
        Cancel
      </button>
      <button text-button variant="primary" size="md" class="decision-dialog-approve-submit" [disable]="submittingDecision()" (click)="submitDecision('approved')">
        Approve
      </button>
    </div>
  </div>
</Dialog>

<Dialog [show]="showResubmitDialog()" (showing)="$event ? showResubmitDialog.set(true) : closeResubmitReviewDialog()">
  <div class="decision-dialog resubmit-dialog">
    <div class="decision-dialog-top">
      <div class="decision-dialog-heading">Review &amp; Resubmit</div>
      <button type="button" class="decision-dialog-close" aria-label="Close" (click)="closeResubmitReviewDialog()">×</button>
    </div>

    @if (resubmitInlineError()) {
      <div class="resubmit-inline-error resubmit-inline-error--dialog">{{ resubmitInlineError() }}</div>
    }

    <label class="decision-dialog-label" for="resubmitReason">Reason <span class="required-marker">*</span></label>
    <select
      id="resubmitReason"
      class="resubmit-reason-select"
      [class.resubmit-reason-select--invalid]="!!getResubmitReasonError()"
      [value]="resubmitReasonCode()"
      [disabled]="submittingResubmit()"
      (change)="onResubmitReasonChange($any($event.target).value)"
      (blur)="resubmitReasonTouched.set(true)"
    >
      <option value="">Select reason...</option>
      @for (option of resubmitReasonOptions(); track option.value) {
        <option [value]="option.value">{{ option.label }}</option>
      }
    </select>
    @if (getResubmitReasonError()) {
      <div class="decision-dialog-error decision-dialog-error--block">{{ getResubmitReasonError() }}</div>
    }

    <label class="decision-dialog-label" for="resubmitNotes">Note (optional)</label>
    <textarea
      id="resubmitNotes"
      class="decision-dialog-textarea"
      [value]="resubmitNotes()"
      [disabled]="submittingResubmit()"
      [attr.maxlength]="resubmitNotesMaxLength"
      (input)="onResubmitNotesChange($any($event.target).value)"
      placeholder="Enter note..."
    ></textarea>
    <div class="decision-dialog-counter">{{ resubmitNotes().length }} / {{ resubmitNotesMaxLength }}</div>

    <div class="decision-dialog-actions">
      <button text-button variant="ghost" size="md" [disable]="submittingResubmit()" (click)="closeResubmitReviewDialog()">
        Cancel
      </button>
      <button text-button variant="primary" size="md" class="decision-dialog-approve-submit" [disable]="submittingResubmit() || !resubmitReasonCode()" (click)="submitResubmitForApproval()">
        Submit
      </button>
    </div>
  </div>
</Dialog>

<Dialog [show]="activeDecisionDialog() === 'rejected'" (showing)="$event ? activeDecisionDialog.set('rejected') : closeDecisionDialog()">
  <div class="decision-dialog decision-dialog--reject">
    <div class="decision-dialog-top">
      <div class="decision-dialog-heading">Reject RMA</div>
      <button type="button" class="decision-dialog-close" aria-label="Close" (click)="closeDecisionDialog()">×</button>
    </div>

    <div class="decision-dialog-message decision-dialog-message--reject">
      <span class="decision-dialog-icon decision-dialog-icon--reject">×</span>
      <div>
        <div class="decision-dialog-title">You are about to reject this RMA.</div>
        <div class="decision-dialog-subtitle">Provide a rejection reason and optional comment.</div>
      </div>
    </div>

    <label class="decision-dialog-label" for="rejectionReason">Rejection Reason <span class="required-marker">*</span></label>
    <textarea
      id="rejectionReason"
      class="decision-dialog-textarea decision-dialog-textarea--reject"
      [class.decision-dialog-textarea--invalid]="!!getRejectionReasonError()"
      [value]="rejectionReason()"
      [disabled]="submittingDecision()"
      [attr.maxlength]="rejectionReasonMaxLength"
      (input)="onRejectionReasonChange($any($event.target).value)"
      (blur)="rejectionReasonTouched.set(true)"
      placeholder="Provide the rejection reason..."
    ></textarea>
    @if (getRejectionReasonError()) {
      <div class="decision-dialog-error decision-dialog-error--block">{{ getRejectionReasonError() }}</div>
    }
    <div class="decision-dialog-counter">{{ rejectionReason().length }} / {{ rejectionReasonMaxLength }}</div>

    <label class="decision-dialog-label" for="rejectDecisionComments">Comment <span class="reject-dialog-optional">(optional)</span></label>
    <textarea
      id="rejectDecisionComments"
      class="decision-dialog-textarea decision-dialog-textarea--reject"
      [value]="decisionComments()"
      [disabled]="submittingDecision()"
      [attr.maxlength]="decisionCommentsMaxLength"
      (input)="onDecisionCommentsChange($any($event.target).value)"
      placeholder="Enter comment..."
    ></textarea>
    <div class="decision-dialog-counter">{{ decisionComments().length }} / {{ decisionCommentsMaxLength }}</div>

    <div class="decision-dialog-actions decision-dialog-actions--reject">
      <button text-button variant="ghost" size="md" class="decision-dialog-reject-cancel" [disable]="submittingDecision()" (click)="closeDecisionDialog()">
        Cancel
      </button>
      <button text-button variant="primary" size="md" class="decision-dialog-reject-submit" [disable]="submittingDecision() || !rejectionReason()" (click)="submitDecision('rejected')">
        Reject
      </button>
    </div>
  </div>
</Dialog>

